import { FC, useState } from 'react';
import { Subscription } from '../../services/service-sales';
import { formatDate, formatDateTime } from '../../util';
import BtButton from '../bt-button/bt-button';
import Modal from '../../components/shared/Modal/Modal';
import { Loading } from '../../components/shared';
import { useTranslation } from 'react-i18next';
import appState from '../../state/AppStateContainer';

type Props = {
  productName: string,
  purchasePage: string,
  subscription?: Subscription,
  loading: boolean,
  onSubscribe: () => void,
  onCancel: () => void,
  onHardCancel?: () => void,
  allUsers?: Map<string, string>,
}

const SubscriptionSettings: FC<Props> = ({ productName, purchasePage, subscription, loading, onSubscribe, onCancel, onHardCancel, allUsers }) => {
  const [showSubscriptionHistory, setShowSubscriptionHistory] = useState(false);
  const [t] = useTranslation('settings', { keyPrefix: 'salesSettings.subscriptions.settings'});
  const isSuperAdmin = appState.isSuperAdmin()

  if (loading) {
    return <Loading type="inline" />
  }

  const num = (s: string | undefined) => s != undefined ? +s : NaN;
  const isActive = !!subscription?.active?.startDate;
  const isCancelled = !subscription?.active?.autoRenew || subscription?.active?.cancelAt;
  const cancelAt = subscription?.active?.cancelAt;
  const endDate = subscription?.active?.endDate;
  const monthlyPrice = num(subscription?.active?.price) / num(subscription?.active?.duration);
  const cancellationDelay = subscription?.active?.cancellationDelay ?? '';
  
  return (<div style={{marginBottom: '16px'}}>
    {isActive ? <>
      {!isCancelled ? (
        <>
          {t('message.active',
            'Prenumeration på "{{productName}}" är aktiv och kommer att förnyas den {{renewalDate}} ({{monthlyPrice}} kr/månad).',
            {
              context: cancellationDelay,
              productName,
              renewalDate: formatDate(endDate),
              monthlyPrice
            }
          )}
          <BtButton 
            onClick={onCancel}
            size="xxs"
            color="white"
            style={{marginLeft: '8px'}}
          >
            {t('button.unsubscribe', 'Avsluta prenumeration')}
          </BtButton>
        </>
      ) : (
        <> 
          {t(`message.canceled.${cancelAt && endDate && cancelAt > endDate ? 'byCancelAt' : 'byEndDate'}`,
            'Prenumeration på "{{productName}}" är uppsagd och kommer inte förnyas efter den {{cancelAt}} ({{monthlyPrice}} kr/månad).',
            {
              context: cancellationDelay,
              productName,
              cancelAt: formatDate(cancelAt),
              endDate: formatDate(endDate),
              monthlyPrice
            }
          )}
          <BtButton 
            onClick={onSubscribe}
            size="xxs"
            color="white"
            style={{marginLeft: '8px'}}
          >
            {t('button.resubscribe', 'Förläng prenumeration')}
          </BtButton>
        </>
      )}
    </> : <>
      {t('message.inactive',
        'Prenumeration på "{{productName}}" är inte aktiv. Den kan köpas på sidan "{{purchasePage}}".',
        {
          productName,
          purchasePage
        }
      )}
    </>}
    {(isActive || subscription?.history?.length) && (
      <BtButton
        onClick={() => setShowSubscriptionHistory(true)}
        size="xxs"
        color="white"
        style={{marginLeft: '8px'}}
      >
        {t('button.showHistory', 'Visa historik')}
      </BtButton>        
    )}
    {isActive && !isCancelled && isSuperAdmin && (
      <BtButton
        onClick={isSuperAdmin ? onHardCancel : () => { }}
        size="xxs"
        color="white"
        style={{ marginLeft: '8px' }}
      >
        {t('button.hardCancel', 'Hårt avbryta')}
      </BtButton>
    )}
    <Modal 
      title={t('history.title', 'Prenumerationshistorik')}
      show={showSubscriptionHistory} 
      setShow={setShowSubscriptionHistory}
      cancelButtonText={t('history.closeButton', 'OK')}
      isScrollable
    >
      <div style={{fontSize: '14px', color: 'var(--gray-700)', maxWidth: '700px'}}>
        {
          subscription?.history?.map((event) => {
            const i18nKeys = {
              ORDER: 'ordered',
              CANCEL: 'canceled',
              CANCEL_AT: 'canceledAt',
              UNCANCEL: 'uncanceled',
              RENEW: 'renewed',
              EXPIRE: 'expired',
            };
            const user = allUsers?.get(event.user) ?? event.user;
            const monthlyPrice = num(event.price) / num(event.duration);
            return (
              <p key={event._id}>
                {t(`history.event.${i18nKeys[event.action] ?? 'UNKNOWN'}${user ? 'By' : ''}`,
                  {
                    context: isNaN(monthlyPrice) ? null : 'withMonthlyPrice',
                    timestamp: formatDateTime(event.date),
                    user,
                    monthlyPrice
                  }
                )}
              </p>
            )
          })
        }
      </div>
    </Modal>
  </div>);
}

export default SubscriptionSettings;